"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DYNAMIC_MERGE_FIELDS = exports.MERGE_FIELD_CATALOG = exports.MergeFieldDefSchema = exports.MERGE_FIELD_SOURCES = exports.MERGE_FIELD_GROUP_LABELS = exports.MERGE_FIELD_GROUPS = void 0;
exports.mergeFieldDef = mergeFieldDef;
exports.isKnownMergeField = isKnownMergeField;
exports.mergeFieldsByGroup = mergeFieldsByGroup;
const zod_1 = require("zod");
/**
 * THE MERGE FIELD CATALOG — one list of every `{{token}}` a legal document
 * may reference, read by three surfaces that used to keep their own copies:
 * the palette a document author picks a field from, the resolver that fills
 * a token at render time, and the tests that pin what the already-seeded
 * documents depend on. One list drifting out of sync with the renderer's
 * token pattern (documents-render/src/merge.ts) is a silent blank in a
 * signed contract — see MissingMergeFieldError there for why that's a defect,
 * not a default.
 *
 * This package is framework-agnostic and consumed by Lambdas as well as UI:
 * no Tailwind classes, no colour values here. A palette maps `group` to a
 * colour in the UI package, not here.
 */
exports.MERGE_FIELD_GROUPS = [
    'company', 'customer', 'signer', 'guarantor',
    'equipment', 'device', 'package', 'finance',
    'vendor', 'employee', 'partner', 'agreement',
];
/** Plain-English label for each group, for a grouped palette heading. */
exports.MERGE_FIELD_GROUP_LABELS = {
    company: 'Our company',
    customer: 'Customer',
    signer: 'Signer',
    guarantor: 'Guarantor',
    equipment: 'Equipment',
    device: 'Device',
    package: 'Software package',
    finance: 'Financing',
    vendor: 'Vendor',
    employee: 'Employee',
    partner: 'Partner',
    agreement: 'Agreement & dates',
};
/**
 * `auto` fields are filled from platform data at render time with no human
 * involved. `guarantor` is the one `manual` group: a personal guarantor's
 * details are prefilled from the owner's record but confirmed by a human at
 * signing — the platform must never assert them unattended.
 *
 * `dynamic` is different in kind from both: its value DOES NOT EXIST YET when
 * the document is rendered. A signing date cannot be known until somebody
 * signs, so a dynamic field prints a fill-in rule on the unsigned copy and
 * its real value only in the executed artifact. It is the one source for
 * which a missing value is not a defect — see `DYNAMIC_MERGE_FIELDS`.
 */
exports.MERGE_FIELD_SOURCES = ['auto', 'manual', 'dynamic'];
/** Same pattern the renderer's `{{token}}` regex accepts. A name that doesn't
 * match this can never resolve — it would render as a literal, untouched
 * placeholder. Keep this in lockstep with `TOKEN` in
 * documents-render/src/merge.ts. */
const MERGE_FIELD_NAME = /^[a-zA-Z][a-zA-Z0-9_]*$/;
exports.MergeFieldDefSchema = zod_1.z.object({
    name: zod_1.z.string().regex(MERGE_FIELD_NAME, 'must match the renderer token pattern ^[a-zA-Z][a-zA-Z0-9_]*$'),
    group: zod_1.z.enum(exports.MERGE_FIELD_GROUPS),
    /** Plain English shown on a chip in the palette, e.g. "Customer legal name" — never the raw token. */
    label: zod_1.z.string().trim().min(1),
    /** A realistic sample value, shown in the palette so an author can see what will appear. */
    example: zod_1.z.string().trim().min(1),
    source: zod_1.z.enum(exports.MERGE_FIELD_SOURCES),
    description: zod_1.z.string().trim().min(1).optional(),
});
/**
 * The full catalog. Examples are coherent with one another: our side is
 * "Ruby Till LLC" (Norfolk, VA — matching the CompanyProfileSchema fixture),
 * the customer is "Joe's Coffee LLC", also in Norfolk, VA.
 */
exports.MERGE_FIELD_CATALOG = [
    // --- company: filled from the Company Identity Master -------------------
    { name: 'company_legal_name', group: 'company', source: 'auto',
        label: 'Company legal name', example: 'Ruby Till LLC',
        resolve: { provider: 'company', key: 'legal_name' },
        description: 'The registered legal entity name, from the Company Identity Master.' },
    { name: 'company_dba', group: 'company', source: 'auto',
        label: 'Company trade name (DBA)', example: 'Ruby Till',
        resolve: { provider: 'company', key: 'dba' },
        fallback: { kind: 'field', field: 'company_legal_name' },
        description: 'The trading name, when it differs from the legal name.' },
    { name: 'company_display_name', group: 'company', source: 'auto',
        label: 'Company display name', example: 'Ruby Till',
        resolve: { provider: 'company', key: 'display_name' },
        description: 'DBA if set, otherwise the legal entity name — the name a customer recognizes.' },
    { name: 'company_entity_type', group: 'company', source: 'auto',
        label: 'Company entity type', example: 'Single-Member LLC',
        resolve: { provider: 'company', key: 'entity_type' } },
    { name: 'company_state_of_formation', group: 'company', source: 'auto',
        label: 'State of formation', example: 'VA',
        resolve: { provider: 'company', key: 'state_of_formation' } },
    { name: 'company_ein', group: 'company', source: 'auto',
        label: 'Company EIN', example: '00-0000000',
        resolve: { provider: 'company', key: 'ein' },
        ask: { askOf: 'operator', question: 'Company EIN', inputType: 'ein', required: true,
            help: 'From the Company Identity Master. Set it in Legal › Company.' } },
    { name: 'company_address', group: 'company', source: 'auto',
        label: 'Company registered address', example: '100 Main St, Suite 2, Norfolk, VA 23510',
        resolve: { provider: 'company', key: 'address' } },
    { name: 'company_mailing_address', group: 'company', source: 'auto',
        label: 'Company mailing address', example: '100 Main St, Norfolk, VA 23510',
        resolve: { provider: 'company', key: 'mailing_address' },
        fallback: { kind: 'field', field: 'company_address' },
        description: 'Falls through to the registered address when no separate mailing address is set.' },
    { name: 'company_city', group: 'company', source: 'auto',
        label: 'Company city', example: 'Norfolk',
        resolve: { provider: 'company', key: 'city' } },
    { name: 'company_state', group: 'company', source: 'auto',
        label: 'Company state', example: 'VA',
        resolve: { provider: 'company', key: 'state' } },
    { name: 'company_postal_code', group: 'company', source: 'auto',
        label: 'Company postal code', example: '23510',
        resolve: { provider: 'company', key: 'postal_code' } },
    { name: 'company_phone', group: 'company', source: 'auto',
        label: 'Company phone', example: '+1-757-555-0100',
        resolve: { provider: 'company', key: 'phone' } },
    { name: 'company_website', group: 'company', source: 'auto',
        label: 'Company website', example: 'https://rubytill.com',
        resolve: { provider: 'company', key: 'website' },
        ask: { askOf: 'operator', question: 'Company website', inputType: 'text', required: true,
            help: 'From the Company Identity Master. Set it in Legal › Company.' } },
    { name: 'company_support_email', group: 'company', source: 'auto',
        label: 'Company support email', example: 'support@rubytill.com',
        resolve: { provider: 'company', key: 'support_email' } },
    { name: 'company_billing_email', group: 'company', source: 'auto',
        label: 'Company billing email', example: 'billing@rubytill.com',
        resolve: { provider: 'company', key: 'billing_email' } },
    { name: 'company_accounts_email', group: 'company', source: 'auto',
        label: 'Company accounts email', example: 'accounts@rubytill.com',
        resolve: { provider: 'company', key: 'accounts_email' } },
    { name: 'company_sales_email', group: 'company', source: 'auto',
        label: 'Company sales email', example: 'sales@rubytill.com',
        resolve: { provider: 'company', key: 'sales_email' } },
    { name: 'company_admin_email', group: 'company', source: 'auto',
        label: 'Company admin email', example: 'admin@rubytill.com',
        resolve: { provider: 'company', key: 'admin_email' } },
    { name: 'company_governing_law_state', group: 'company', source: 'auto',
        label: 'Governing law state', example: 'VA',
        resolve: { provider: 'company', key: 'governing_law_state' },
        description: 'The state whose law governs the agreement.' },
    { name: 'company_venue_county', group: 'company', source: 'auto',
        label: 'Venue county', example: 'City of Norfolk',
        resolve: { provider: 'company', key: 'venue_county' },
        description: 'The county named for venue in dispute-resolution clauses.' },
    { name: 'company_signatory_name', group: 'company', source: 'auto',
        label: 'Who signs for us', example: 'Dana Reyes',
        resolve: { provider: 'company', key: 'signatory_name' },
        description: 'The authorised signatory countersigning this agreement — the profile\'s '
            + 'default, or the specific person this agreement named.' },
    { name: 'company_signatory_title', group: 'company', source: 'auto',
        label: 'Their title', example: 'Chief Executive Officer',
        resolve: { provider: 'company', key: 'signatory_title' } },
    // --- customer: tenant record, falling through to the named location -----
    { name: 'customer_legal_name', group: 'customer', source: 'auto',
        label: 'Customer legal name', example: "Joe's Coffee LLC",
        resolve: { provider: 'customer', key: 'legal_name' },
        aliases: ['merchantLegalName'] },
    { name: 'customer_dba', group: 'customer', source: 'auto',
        label: 'Customer trade name (DBA)', example: "Joe's Coffee",
        resolve: { provider: 'customer', key: 'dba' },
        aliases: ['businessName'] },
    { name: 'customer_ein', group: 'customer', source: 'auto',
        label: 'Customer EIN', example: '11-1111111',
        resolve: { provider: 'customer', key: 'ein' },
        ask: { askOf: 'customer', question: 'Business EIN', inputType: 'ein', required: true,
            help: 'As shown on your IRS CP-575 letter.' } },
    { name: 'customer_address', group: 'customer', source: 'auto',
        label: 'Customer address', example: '200 Granby St, Norfolk, VA 23510',
        resolve: { provider: 'customer', key: 'address' } },
    { name: 'customer_city', group: 'customer', source: 'auto',
        label: 'Customer city', example: 'Norfolk',
        resolve: { provider: 'customer', key: 'city' } },
    { name: 'customer_state', group: 'customer', source: 'auto',
        label: 'Customer state', example: 'VA',
        resolve: { provider: 'customer', key: 'state' } },
    { name: 'customer_postal_code', group: 'customer', source: 'auto',
        label: 'Customer postal code', example: '23510',
        resolve: { provider: 'customer', key: 'postal_code' } },
    { name: 'customer_phone', group: 'customer', source: 'auto',
        label: 'Customer phone', example: '+1-757-555-0199',
        resolve: { provider: 'customer', key: 'phone' } },
    { name: 'customer_email', group: 'customer', source: 'auto',
        label: 'Customer email', example: 'joe@joescoffee.com',
        resolve: { provider: 'customer', key: 'email' },
        aliases: ['merchantEmail'] },
    { name: 'customer_business_type', group: 'customer', source: 'auto',
        label: 'Customer business type', example: 'Coffee shop',
        resolve: { provider: 'customer', key: 'business_type' } },
    { name: 'customer_location_name', group: 'customer', source: 'auto',
        label: 'Customer location name', example: "Joe's Coffee — Granby St",
        resolve: { provider: 'customer', key: 'location_name' },
        description: 'The location the deal names, when the tenant has more than one.' },
    // --- signer: the authenticated user signing ------------------------------
    { name: 'signer_name', group: 'signer', source: 'auto',
        label: 'Signer name', example: 'Joe Martinez',
        resolve: { provider: 'signer', key: 'name' } },
    { name: 'signer_email', group: 'signer', source: 'auto',
        label: 'Signer email', example: 'joe@joescoffee.com',
        resolve: { provider: 'signer', key: 'email' } },
    { name: 'signer_title', group: 'signer', source: 'auto',
        label: 'Signer title', example: 'Owner',
        resolve: { provider: 'signer', key: 'title' } },
    { name: 'signer_phone', group: 'signer', source: 'auto',
        label: 'Signer phone', example: '+1-757-555-0199',
        resolve: { provider: 'signer', key: 'phone' } },
    // --- guarantor: MANUAL — prefilled, but confirmed by a human at signing --
    { name: 'guarantor_name', group: 'guarantor', source: 'manual',
        label: 'Guarantor name', example: 'Joe Martinez',
        resolve: { provider: 'guarantor', key: 'name' },
        ask: { askOf: 'signer', question: 'Full legal name of the guarantor', inputType: 'text', required: true },
        description: 'Prefilled from the owner\'s record; the signer confirms it at signing.' },
    { name: 'guarantor_address', group: 'guarantor', source: 'manual',
        label: 'Guarantor address', example: '15 Colley Ave, Norfolk, VA 23507',
        resolve: { provider: 'guarantor', key: 'address' },
        ask: { askOf: 'signer', question: 'Guarantor home address', inputType: 'address', required: true,
            help: 'A personal guaranty binds your own assets, so this must be your home address, not the business address.' },
        description: 'The guarantor\'s personal address, confirmed by a human at signing.' },
    { name: 'guarantor_phone', group: 'guarantor', source: 'manual',
        label: 'Guarantor phone', example: '+1-757-555-0142',
        resolve: { provider: 'guarantor', key: 'phone' },
        ask: { askOf: 'signer', question: 'Guarantor phone', inputType: 'phone', required: true },
        description: 'The guarantor\'s personal phone, confirmed by a human at signing.' },
    { name: 'guarantor_email', group: 'guarantor', source: 'manual',
        label: 'Guarantor email', example: 'joe.martinez@gmail.com',
        resolve: { provider: 'guarantor', key: 'email' },
        ask: { askOf: 'signer', question: 'Guarantor email', inputType: 'email', required: true },
        description: 'The guarantor\'s personal email, confirmed by a human at signing.' },
    // --- equipment: from the order's line items ------------------------------
    { name: 'equipment_summary', group: 'equipment', source: 'auto',
        label: 'Equipment summary', example: '2x K10 POS Terminal, 1x TC-TOUCH-A10 Kiosk',
        resolve: { provider: 'equipment', key: 'summary' },
        aliases: ['devicesList'] },
    { name: 'equipment_count', group: 'equipment', source: 'auto',
        label: 'Equipment count', example: '3',
        resolve: { provider: 'equipment', key: 'count' } },
    { name: 'equipment_total_price', group: 'equipment', source: 'auto',
        label: 'Equipment total price', example: '$4,497.00',
        resolve: { provider: 'equipment', key: 'total_price' } },
    // --- device: THE specific hardware, where equipment_* is the roll-up ------
    // The singular fields resolve only when the order carries exactly one
    // device — see deviceValues. On a multi-device order they are deliberately
    // missing rather than silently describing the first line.
    { name: 'device_model', group: 'device', source: 'auto',
        label: 'Device model', example: 'K10 POS Terminal',
        resolve: { provider: 'device', key: 'model' },
        description: 'The device on a single-device order, and every device named in order on a larger one. '
            + 'Use the equipment schedule when the contract needs each one\'s price or terms.' },
    { name: 'device_manufacturer', group: 'device', source: 'auto',
        label: 'Device manufacturer', example: 'Dejavoo',
        resolve: { provider: 'device', key: 'manufacturer' } },
    { name: 'device_bundle_name', group: 'device', source: 'auto',
        label: 'Bundle name', example: 'Counter Starter Kit',
        resolve: { provider: 'device', key: 'bundle_name' },
        description: 'Set when the device was sold as a named kit rather than a bare model.' },
    { name: 'device_bundle_contents', group: 'device', source: 'auto',
        label: 'Bundle contents', example: 'K10 Terminal, Cash Drawer, 2x Thermal Printer',
        resolve: { provider: 'device', key: 'bundle_contents' } },
    { name: 'device_warranty_months', group: 'device', source: 'auto',
        label: 'Warranty (months)', example: '12',
        resolve: { provider: 'device', key: 'warranty_months' } },
    { name: 'device_quantity', group: 'device', source: 'auto',
        label: 'Device quantity', example: '2',
        resolve: { provider: 'device', key: 'quantity' },
        description: 'Total units across every device line — an aggregate, always available.' },
    { name: 'device_unit_price', group: 'device', source: 'auto',
        label: 'Device unit price', example: '$899.00',
        resolve: { provider: 'device', key: 'unit_price' } },
    { name: 'device_line_total', group: 'device', source: 'auto',
        label: 'Device line total', example: '$1,798.00',
        resolve: { provider: 'device', key: 'line_total' } },
    { name: 'device_sale_mode', group: 'device', source: 'auto',
        label: 'How the device was sold', example: 'Financed over 24 months',
        resolve: { provider: 'device', key: 'sale_mode' } },
    // ── dynamic: allocated at provisioning, so absent on every unsigned copy ──
    { name: 'device_serial', group: 'device', source: 'dynamic',
        label: 'Device serial number', example: 'K10-4471-9920',
        resolve: { provider: 'device', key: 'serial' },
        ask: { askOf: 'operator', question: 'Device serial number', inputType: 'text', required: true },
        fallback: { kind: 'blankLine', chars: 24 },
        description: 'Does not exist until a unit is allocated — prints as a blank on the unsigned copy.' },
    { name: 'device_terminal_id', group: 'device', source: 'dynamic',
        label: 'Terminal ID (TID)', example: 'TID 88104420',
        resolve: { provider: 'device', key: 'terminal_id' },
        ask: { askOf: 'operator', question: 'Terminal ID (TID)', inputType: 'text', required: true },
        fallback: { kind: 'blankLine', chars: 24 },
        description: 'Assigned by the processor at boarding — absent until then.' },
    // --- package: the software the deal sells ---------------------------------
    { name: 'package_name', group: 'package', source: 'auto',
        label: 'Package name', example: 'RubyTill Pro',
        resolve: { provider: 'package', key: 'name' },
        description: 'Only resolves for a single-package deal; use the package list or schedule otherwise.' },
    { name: 'package_name_list', group: 'package', source: 'auto',
        label: 'All package names', example: 'RubyTill Pro, Loyalty Add-on',
        resolve: { provider: 'package', key: 'name_list' },
        description: 'Every package on the deal, named in order — truthful whether there is one or several.' },
    { name: 'package_monthly_price', group: 'package', source: 'auto',
        label: 'Package monthly price', example: '$149.00',
        resolve: { provider: 'package', key: 'monthly_price' } },
    { name: 'package_feature_list', group: 'package', source: 'auto',
        label: 'Package features', example: 'Inventory, Loyalty, Online Ordering',
        resolve: { provider: 'package', key: 'feature_list' } },
    { name: 'package_limits', group: 'package', source: 'auto',
        label: 'Package limits', example: '5 terminals, 3 locations',
        resolve: { provider: 'package', key: 'limits' } },
    { name: 'package_count', group: 'package', source: 'auto',
        label: 'Number of packages', example: '2',
        resolve: { provider: 'package', key: 'count' } },
    { name: 'package_monthly_total', group: 'package', source: 'auto',
        label: 'Total monthly software', example: '$298.00',
        resolve: { provider: 'package', key: 'monthly_total' } },
    // --- vendor: who we buy from or contract with -----------------------------
    { name: 'vendor_legal_name', group: 'vendor', source: 'auto',
        label: 'Vendor legal name', example: 'Dejavoo Systems Inc.',
        resolve: { provider: 'vendor', key: 'legal_name' } },
    { name: 'vendor_dba', group: 'vendor', source: 'auto',
        label: 'Vendor trade name (DBA)', example: 'Dejavoo',
        resolve: { provider: 'vendor', key: 'dba' },
        fallback: { kind: 'field', field: 'vendor_legal_name' } },
    { name: 'vendor_contact_name', group: 'vendor', source: 'auto',
        label: 'Vendor contact name', example: 'Priya Raman',
        resolve: { provider: 'vendor', key: 'contact_name' } },
    { name: 'vendor_contact_email', group: 'vendor', source: 'auto',
        label: 'Vendor contact email', example: 'ap@dejavoo.example',
        resolve: { provider: 'vendor', key: 'contact_email' } },
    { name: 'vendor_contact_phone', group: 'vendor', source: 'auto',
        label: 'Vendor contact phone', example: '+1-757-555-0142',
        resolve: { provider: 'vendor', key: 'contact_phone' } },
    { name: 'vendor_address', group: 'vendor', source: 'auto',
        label: 'Vendor address', example: '4 Industrial Way, Norfolk, VA 23510',
        resolve: { provider: 'vendor', key: 'address' } },
    { name: 'vendor_ein', group: 'vendor', source: 'auto',
        label: 'Vendor EIN', example: '00-0000000',
        resolve: { provider: 'vendor', key: 'ein' },
        ask: { askOf: 'operator', question: 'Vendor EIN', inputType: 'ein', required: true } },
    { name: 'vendor_account_no', group: 'vendor', source: 'auto',
        label: 'Our account number with them', example: 'RT-88104',
        resolve: { provider: 'vendor', key: 'account_no' } },
    { name: 'vendor_payment_terms', group: 'vendor', source: 'auto',
        label: 'Vendor payment terms', example: 'Net 30',
        resolve: { provider: 'vendor', key: 'payment_terms' } },
    { name: 'vendor_category', group: 'vendor', source: 'auto',
        label: 'Vendor category', example: 'Hardware supplier',
        resolve: { provider: 'vendor', key: 'category' } },
    // --- employee: the person being hired -------------------------------------
    // The EMPLOYER on these documents is the tenant — the `customer_*` fields.
    // See CONTEXT_PROVIDERS.employment, which omits `company` on purpose.
    { name: 'employee_full_name', group: 'employee', source: 'auto',
        label: 'Employee full name', example: 'Dana Whitfield',
        resolve: { provider: 'employee', key: 'full_name' } },
    { name: 'employee_job_title', group: 'employee', source: 'auto',
        label: 'Job title', example: 'Shift Supervisor',
        resolve: { provider: 'employee', key: 'job_title' } },
    { name: 'employee_employment_type', group: 'employee', source: 'auto',
        label: 'Employment type', example: 'Full-time, hourly',
        resolve: { provider: 'employee', key: 'employment_type' } },
    { name: 'employee_pay_rate', group: 'employee', source: 'auto',
        label: 'Pay rate', example: '$18.50/hour',
        resolve: { provider: 'employee', key: 'pay_rate' } },
    { name: 'employee_start_date', group: 'employee', source: 'auto',
        label: 'Start date', example: 'September 15, 2026',
        resolve: { provider: 'employee', key: 'start_date' } },
    { name: 'employee_location_name', group: 'employee', source: 'auto',
        label: 'Assigned location', example: 'Norfolk — Granby St',
        resolve: { provider: 'employee', key: 'location_name' } },
    { name: 'employee_manager_name', group: 'employee', source: 'auto',
        label: 'Reports to', example: 'Alex Moreno',
        resolve: { provider: 'employee', key: 'manager_name' } },
    { name: 'employee_email', group: 'employee', source: 'auto',
        label: 'Employee email', example: 'dana@joescoffee.example',
        resolve: { provider: 'employee', key: 'email' } },
    { name: 'employee_phone', group: 'employee', source: 'auto',
        label: 'Employee phone', example: '+1-757-555-0198',
        resolve: { provider: 'employee', key: 'phone' } },
    // --- partner: the ISO / affiliate -----------------------------------------
    { name: 'partner_legal_name', group: 'partner', source: 'auto',
        label: 'Partner legal name', example: 'Coastal Merchant Services LLC',
        resolve: { provider: 'partner', key: 'legal_name' } },
    { name: 'partner_dba', group: 'partner', source: 'auto',
        label: 'Partner trade name (DBA)', example: 'Coastal MS',
        resolve: { provider: 'partner', key: 'dba' },
        fallback: { kind: 'field', field: 'partner_legal_name' } },
    { name: 'partner_contact_name', group: 'partner', source: 'auto',
        label: 'Partner contact name', example: 'Sam Oyelaran',
        resolve: { provider: 'partner', key: 'contact_name' } },
    { name: 'partner_contact_email', group: 'partner', source: 'auto',
        label: 'Partner contact email', example: 'sam@coastalms.example',
        resolve: { provider: 'partner', key: 'contact_email' } },
    { name: 'partner_address', group: 'partner', source: 'auto',
        label: 'Partner address', example: '88 Bay Ave, Virginia Beach, VA 23451',
        resolve: { provider: 'partner', key: 'address' } },
    { name: 'partner_referral_code', group: 'partner', source: 'auto',
        label: 'Referral code', example: 'COASTAL-2026',
        resolve: { provider: 'partner', key: 'referral_code' } },
    { name: 'partner_commission_plan', group: 'partner', source: 'auto',
        label: 'Commission plan', example: 'Standard ISO — 20% residual',
        resolve: { provider: 'partner', key: 'commission_plan' } },
    { name: 'partner_payout_terms', group: 'partner', source: 'auto',
        label: 'Payout terms', example: 'Monthly, net 15, after merchant funding',
        resolve: { provider: 'partner', key: 'payout_terms' } },
    { name: 'partner_tier', group: 'partner', source: 'auto',
        label: 'Partner tier', example: 'Gold',
        resolve: { provider: 'partner', key: 'tier' } },
    // --- finance: from the selected financing policy --------------------------
    { name: 'finance_term_months', group: 'finance', source: 'auto',
        label: 'Financing term (months)', example: '36',
        resolve: { provider: 'finance', key: 'term_months' },
        aliases: ['termsTermMonths'] },
    { name: 'finance_payment_amount', group: 'finance', source: 'auto',
        label: 'Financing payment amount', example: '$149.00/mo',
        resolve: { provider: 'finance', key: 'payment_amount' },
        aliases: ['termsMonthly'] },
    { name: 'finance_deposit_amount', group: 'finance', source: 'auto',
        label: 'Financing deposit amount', example: '$500.00',
        resolve: { provider: 'finance', key: 'deposit_amount' },
        fallback: { kind: 'literal', value: '$0.00' } },
    { name: 'finance_buyout_amount', group: 'finance', source: 'auto',
        label: 'Financing buyout amount', example: '$1.00',
        resolve: { provider: 'finance', key: 'buyout_amount' },
        description: 'The amount due to own the equipment outright at the end of the term.' },
    { name: 'finance_first_payment_date', group: 'finance', source: 'auto',
        label: 'First payment date', example: 'September 26, 2026',
        resolve: { provider: 'finance', key: 'first_payment_date' } },
    { name: 'finance_total_of_payments', group: 'finance', source: 'auto',
        label: 'Total of payments', example: '$5,364.00',
        resolve: { provider: 'finance', key: 'total_of_payments' },
        aliases: ['termsTotal'] },
    { name: 'finance_early_payoff_terms', group: 'finance', source: 'auto',
        label: 'Early payoff terms', example: 'the remaining balance, with no penalty',
        resolve: { provider: 'finance', key: 'early_payoff_terms' },
        aliases: ['policyEarlyPayoff'] },
    { name: 'finance_default_terms', group: 'finance', source: 'auto',
        label: 'Default terms', example: 'a 10-day grace period, after which suspension applies',
        resolve: { provider: 'finance', key: 'default_terms' },
        aliases: ['policyDefault'] },
    // --- agreement: generated at freeze time ----------------------------------
    { name: 'agreement_no', group: 'agreement', source: 'auto',
        label: 'Agreement number', example: 'AGR-000482',
        resolve: { provider: 'agreement', key: 'no' } },
    { name: 'agreement_date', group: 'agreement', source: 'auto',
        label: 'Agreement date', example: 'August 29, 2026',
        resolve: { provider: 'agreement', key: 'date' } },
    // ── dynamic: not known until the parties sign ───────────────────────────
    { name: 'agreement_execution_date', group: 'agreement', source: 'dynamic',
        label: 'Execution date', example: 'September 3, 2026',
        resolve: { provider: 'agreement', key: 'execution_date' },
        // documents-ext-service signing.ts emits the BARE name; keep it resolvable.
        aliases: ['execution_date'],
        ask: { askOf: 'signer', question: 'Execution date', inputType: 'date', required: true },
        fallback: { kind: 'blankLine', chars: 24 },
        description: 'The date the LAST party signed — when the agreement became fully executed.' },
    { name: 'agreement_execution_time', group: 'agreement', source: 'dynamic',
        label: 'Execution time', example: '2:41 PM UTC',
        resolve: { provider: 'agreement', key: 'execution_time' },
        // documents-ext-service signing.ts emits the BARE name; keep it resolvable.
        aliases: ['execution_time'],
        ask: { askOf: 'signer', question: 'Execution time', inputType: 'text', required: true },
        fallback: { kind: 'blankLine', chars: 24 },
        description: 'The time the last signature was captured.' },
    { name: 'agreement_customer_signed_date', group: 'agreement', source: 'dynamic',
        label: 'Customer signed date', example: 'September 2, 2026',
        resolve: { provider: 'agreement', key: 'customer_signed_date' },
        // documents-ext-service signing.ts emits the BARE name; keep it resolvable.
        aliases: ['customer_signed_date'],
        ask: { askOf: 'signer', question: 'Customer signed date', inputType: 'date', required: true },
        fallback: { kind: 'blankLine', chars: 24 },
        description: 'The date the customer signed.' },
    { name: 'agreement_company_signed_date', group: 'agreement', source: 'dynamic',
        label: 'Our signed date', example: 'September 3, 2026',
        resolve: { provider: 'agreement', key: 'company_signed_date' },
        // documents-ext-service signing.ts emits the BARE name; keep it resolvable.
        aliases: ['company_signed_date'],
        ask: { askOf: 'signer', question: 'Our signed date', inputType: 'date', required: true },
        fallback: { kind: 'blankLine', chars: 24 },
        description: 'The date we countersigned.' },
    { name: 'agreement_guarantor_signed_date', group: 'agreement', source: 'dynamic',
        label: 'Guarantor signed date', example: 'September 2, 2026',
        resolve: { provider: 'agreement', key: 'guarantor_signed_date' },
        // documents-ext-service signing.ts emits the BARE name; keep it resolvable.
        aliases: ['guarantor_signed_date'],
        ask: { askOf: 'signer', question: 'Guarantor signed date', inputType: 'date', required: true },
        fallback: { kind: 'blankLine', chars: 24 },
        description: 'The date the personal guarantor signed.' },
    { name: 'effective_date', group: 'agreement', source: 'auto',
        label: 'Effective date', example: 'September 1, 2026',
        resolve: { provider: 'agreement', key: 'effective_date' },
        description: 'Deliberately not prefixed "agreement_" — 15 already-seeded documents reference '
            + 'it by this exact name; do not rename.' },
];
/** Look up one field definition by name, or `undefined` if it isn't in the catalog. */
function mergeFieldDef(name) {
    return exports.MERGE_FIELD_CATALOG.find(d => d.name === name);
}
/** Whether `name` is a recognized merge field. */
function isKnownMergeField(name) {
    return mergeFieldDef(name) !== undefined;
}
/** The catalog grouped for a palette, each group's fields in catalog order. */
/**
 * THE FIELDS WHOSE VALUE CANNOT EXIST AT RENDER TIME.
 *
 * Derived from the catalog rather than hand-listed, so adding a `dynamic`
 * entry above is all it takes — a second list would be one more thing to
 * forget.
 *
 * The renderers read this to decide that a MISSING value is acceptable for
 * these names and only these. That exemption is narrow on purpose: outside
 * it, a blank where a party's legal name belongs is still a defect.
 */
exports.DYNAMIC_MERGE_FIELDS = new Set(exports.MERGE_FIELD_CATALOG.filter((d) => d.source === 'dynamic').map((d) => d.name));
function mergeFieldsByGroup() {
    const grouped = Object.fromEntries(exports.MERGE_FIELD_GROUPS.map(group => [group, []]));
    for (const def of exports.MERGE_FIELD_CATALOG)
        grouped[def.group].push(def);
    return grouped;
}
//# sourceMappingURL=mergeFieldCatalog.js.map